import { X, CirclePlus, CircleMinus, InfoIcon } from "lucide-react";
import React from "react";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@components/ui/select";
import { Input } from "@components/ui/input";
import { DATE_TIME_FORMAT, PAGINATIONOPTION } from "./allData";
import FilterationBlock from "./FilterationBlock";
import CustomTooltip from "@components/common/CustomTooltip";

type FormRow = {
  paginationEnable: boolean;
  categoryTypeKey: string;
  categoryTypeValue: string;
  paginationTypeKey: string;
  paginationTypeValue: string;
  paramKey?: string;
  paramValue?: string;
  categoryTypeCustom?: string;
  paginationTypeCustom?: string;
  limitKey: string;
  limitValue: string;
  limitCustomKey: string;
  cursorKey: string;
  cursorValue: string;
  cursorSourcePath: string;
  // Token fields
  tokenKey: string;
  tokenValue: string;
  tokenSourcePath: string;
  // Next URL fields
  nextUrlKey: string;
  nextUrlValue: string;
  nextUrlSourcePath: string;
};

type QueryParam = {
  enabled: boolean;
  key: string;
  value: string;
};

type PaginationBlockProps = {
  formData: FormRow[];
  onChange: (
    index: number,
    field: keyof FormRow,
    value: boolean | string,
  ) => void;
  onAddRow: () => void;
  onRemoveRow: (index: number) => void;
  onUrlChange?: (queryParams: string) => void;
  setFormData: (data: FormRow[]) => void;

  visible: boolean;
  showQueryParams: boolean;
  setShowQueryParams: (show: boolean) => void;
  queryParams: QueryParam[];
  onQueryParamChange: (
    index: number,
    field: "enabled" | "key" | "value",
    value: boolean | string,
  ) => void;
  onAddQueryParam: () => void;
  onRemoveQueryParam: (index: number) => void;
  onClearQueryParams: () => void;
  paginationType: string;
  handlePaginationTypeChange: (value: string) => void;
  selectedLogicName?: string | null;
  filterationType: string;
  onFilterationChange: (
    type: string,
    startKey: string,
    endKey: string,
    customFormat: string,
    logCollectionPeriod: string,
  ) => void;
  // Saved filteration data for prefilling
  savedStartKey?: string;
  savedEndKey?: string;
  savedCustomDateFormat?: string;
  savedLogCollectionPeriod?: string;
  // Saved pagination data for prefilling
  savedPaginationType?: string;
  savedPaginationFormData?: FormRow[];
  savedQueryParams?: QueryParam[];
  requestId?: number | undefined;
};

export default function PaginationBlock({
  paginationType,
  formData,
  onChange,
  onAddRow,
  onRemoveRow,
  onUrlChange,
  setFormData,
  visible,
  showQueryParams,
  setShowQueryParams,
  queryParams,
  onQueryParamChange,
  onAddQueryParam,
  onRemoveQueryParam,
  onClearQueryParams,
  handlePaginationTypeChange,
  selectedLogicName,
  filterationType,
  onFilterationChange,
  savedStartKey,
  savedEndKey,
  savedCustomDateFormat,
  savedLogCollectionPeriod,
  savedPaginationType,
  savedPaginationFormData,
  savedQueryParams,
  requestId,
}: PaginationBlockProps) {
  if (!visible) return null;

  // Track selection order locally
  const selectionOrderRef = React.useRef<Map<string, number>>(new Map());
  const lastRestoredType = React.useRef<string>("");
  const lastRestoredData = React.useRef<string>("");
  const lastRestoredRequestId = React.useRef<number | undefined>(undefined);
  // Track previous filteration type to prevent unnecessary updates
  const prevFilterationType = React.useRef(filterationType);

  // Update ref when filterationType changes from props
  React.useEffect(() => {
    prevFilterationType.current = filterationType;
  }, [filterationType]);

  // Keep Query Parameters checkbox in sync with loaded data.
  // This also handles async updates where savedQueryParams arrives after requestId.
  React.useEffect(() => {
    const isNewRequest = lastRestoredRequestId.current !== requestId;
    const hasData =
      savedQueryParams?.some(
        (p) => (p.key && p.key.trim()) || (p.value && p.value.trim()),
      ) || false;

    // Only sync on request switch; do not force-toggle during manual user interaction.
    if (isNewRequest && requestId !== undefined) {
      lastRestoredRequestId.current = requestId;

      // For a new request, pre-check only when there is prefilled data.
      setShowQueryParams(hasData);
    }
  }, [requestId, savedQueryParams, showQueryParams, setShowQueryParams]);

  // Restore saved pagination data when saved props change
  React.useEffect(() => {
    // Create a unique signature of the saved data
    const savedDataSignature =
      savedPaginationType + JSON.stringify(savedPaginationFormData);
    // Skip if we've already restored this exact data
    if (
      lastRestoredType.current === savedPaginationType &&
      lastRestoredData.current === savedDataSignature
    ) {
      return;
    }
    // Skip if there's no actual data to restore
    if (
      !savedPaginationType ||
      savedPaginationType === "" ||
      savedPaginationType === "none"
    ) {
      // Update refs even for empty data to prevent re-checking
      lastRestoredType.current = savedPaginationType || "";
      lastRestoredData.current = savedDataSignature;
      return;
    }

    // Check if the current state is already in sync with saved data
    if (
      paginationType === savedPaginationType &&
      JSON.stringify(formData) === JSON.stringify(savedPaginationFormData)
    ) {
      // Already in sync, just update the refs
      lastRestoredType.current = savedPaginationType;
      lastRestoredData.current = savedDataSignature;
      return;
    }

    // Update the refs first to prevent re-running
    lastRestoredType.current = savedPaginationType;
    lastRestoredData.current = savedDataSignature;

    // Perform restoration
    if (savedPaginationType !== paginationType) {
      handlePaginationTypeChange(savedPaginationType);
    }

    if (
      savedPaginationFormData &&
      savedPaginationFormData.length > 0 &&
      JSON.stringify(formData) !== JSON.stringify(savedPaginationFormData)
    ) {
      setFormData(savedPaginationFormData);
    }
  }, [savedPaginationType, savedPaginationFormData]);

  const isOffsetLimit = paginationType === "offset_limit";
  const isPageSize = paginationType === "page_page_size";
  const isCursor = paginationType === "cursor";
  const isToken = paginationType === "token";
  const isNext = paginationType === "next_url";
  const isManualKeyValue = ["header", "time", "manual"].includes(
    paginationType,
  );
  const needsVerticalLayout = [
    "offset_limit",
    "page_page_size",
    "cursor",
    "token",
    "next_url",
    // "header",
    // "time",
  ].includes(paginationType);

  // Helper function to encode params while preserving template variables
  const encodeParam = (value: string): string => {
    if (!value) return "";
    // Check if the entire value is a template variable
    if (/^\{\{[^}]+\}\}$/.test(value.trim())) {
      return value; // Don't encode template variables
    }
    // If it contains template variables mixed with other content
    if (value.includes("{{")) {
      return value; // Don't encode if it has any template syntax
    }
    return encodeURIComponent(value);
  };

  // Auto-update URL from Query Params only.
  // Pagination and filteration must never be serialized into URL query params.
  React.useEffect(() => {
    const queryParamsList: string[] = [];

    const shouldIncludeQueryParams =
      selectedLogicName !== "Save Logs" && selectedLogicName !== "Data List"
        ? true
        : showQueryParams;

    // First, collect Query Params (these go first in URL after ?)
    if (shouldIncludeQueryParams) {
      queryParams.forEach((item) => {
        if (item.enabled && item.key) {
          // Include params even if value is empty (allows ?key= or ?key=value)
          queryParamsList.push(
            `${encodeParam(item.key)}=${encodeParam(item.value || "")}`,
          );
        }
      });
    }

    onUrlChange?.(queryParamsList.join("&"));
  }, [queryParams, showQueryParams, selectedLogicName]);

  // Clear pagination data
  const clearPagination = () => {
    selectionOrderRef.current.clear();
    setFormData([
      {
        paginationEnable: true,
        categoryTypeKey: "",
        categoryTypeValue: "",
        paginationTypeKey: "",
        paginationTypeValue: "",
        paramKey: "",
        paramValue: "",
        categoryTypeCustom: "",
        paginationTypeCustom: "",
        limitKey: "",
        limitValue: "",
        limitCustomKey: "",
        cursorKey: "",
        cursorValue: "",
        cursorSourcePath: "",
        tokenKey: "",
        tokenValue: "",
        tokenSourcePath: "",
        nextUrlKey: "",
        nextUrlValue: "",
        nextUrlSourcePath: "",
      },
    ]);
  };

  // Clear query parameters
  const clearQueryParam = () => {
    onClearQueryParams();
  };

  // handle the filteration change - Don't clear existing values when type changes
  const handleFilterationChange = (value: string) => {
    // Only call if the type actually changed from what we know
    // But allow it if it's coming from user interaction (Select change)
    const hasActuallyChanged = prevFilterationType.current !== value;

    if (hasActuallyChanged) {
      prevFilterationType.current = value;

      // When filtration type changes, keep existing values
      // FilterationBlock will manage the actual field values
      onFilterationChange(
        value,
        savedStartKey || "",
        savedEndKey || "",
        savedCustomDateFormat || "",
        savedLogCollectionPeriod || "",
      );
    }
  };

  // Toggle query params visibility
  const toggleQueryParams = () => {
    const next = !showQueryParams;
    setShowQueryParams(next);

    // For Save Logs/Data List: when hidden, clear query params so URL and payload are cleaned.
    if (
      !next &&
      (selectedLogicName === "Save Logs" || selectedLogicName === "Data List")
    ) {
      onClearQueryParams();
    }
  };

  return (
    <div>
      {/* Query Params Toggle */}

      {(selectedLogicName == "Save Logs" ||
        selectedLogicName == "Data List") && (
        <div className="flex items-center justify-between gap-2 px-1 py-1">
          <p className={`font-semibold text-sm text-muted-foreground `}>
            Params Options
          </p>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={showQueryParams}
              onChange={toggleQueryParams}
              className="h-4 w-4 cursor-pointer"
            />
            <div
              className="cursor-pointer select-none"
              onClick={toggleQueryParams}
            >
              <p className="text-sm text-muted-foreground">
                {!showQueryParams ? "Show" : "Hide"} Query Parameters
              </p>
            </div>
          </div>
        </div>
      )}

      {selectedLogicName === "Save Logs" ||
      selectedLogicName === "Data List" ? (
        <div className="mt-1 rounded-lg border border-lightGray bg-white/60 backdrop-blur-md shadow-sm px-3 py-2 h-[80px]">
          <div className="grid grid-cols-3 md:grid-cols-3 gap-6 items-start">
            {/* Pagination */}
            <div className="flex flex-col gap-1 h-[72px]">
              <label className="text-xs font-semibold text-muted-foreground ml-1">
                Pagination Type
              </label>

              <Select
                value={paginationType || "none"}
                onValueChange={(v) => {
                  const value = v === "none" ? "none" : v;
                  handlePaginationTypeChange(value);
                }}
              >
                <SelectTrigger className="w-full rounded-xl">
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  {PAGINATIONOPTION.map((m) => (
                    <SelectItem key={m.value} value={m.value}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Time Format */}
            <div className="flex flex-col gap-1 h-[72px]">
              <label className="text-xs font-semibold text-muted-foreground ml-1">
                Time Format
              </label>

              <Select
                value={filterationType || "none"}
                onValueChange={handleFilterationChange}
              >
                <SelectTrigger className="w-full rounded-xl">
                  <SelectValue placeholder="Select time format">
                    {DATE_TIME_FORMAT.find((m) => m.value === filterationType)
                      ?.label || "Select time format"}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {DATE_TIME_FORMAT.map((m) => (
                    <SelectItem key={m.value} value={m.value}>
                      <div className="flex flex-col">
                        <span>{m.label}</span>
                        {m.example && (
                          <span className="text-xs text-muted-foreground">
                            ({m.example})
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      ) : (
        ""
      )}

      {/* query params section */}

      {(selectedLogicName !== "Save Logs" &&
        selectedLogicName !== "Data List") ||
      (visible && showQueryParams) ? (
        <>
          <span className="font-bold text-md ml-1 mt-3 text-primaryAmber block">
            Query Params
          </span>
          <div className="border bg-lightGray rounded-lg px-4 py-2 mt-1 flex flex-col gap-1 ">
            <div className="grid grid-cols-[auto_1fr_1fr_auto] gap-2 mb-2 text-xs font-semibold text-muted-foreground ml-2 mt-1">
              <div className="w-6" />
              <div>Key</div>
              <div>Value</div>
              <div className="w-6" />
            </div>

            <div className="border-b border-t py-2">
              {queryParams.map((item, index) => (
                <div
                  key={index}
                  className="grid gap-3 rounded-md bg-muted/50 transition-all grid-cols-[auto_1fr_1fr_auto] items-center px-2 mb-1"
                >
                  <div className="flex items-center justify-center">
                    <input
                      type="checkbox"
                      checked={item.enabled}
                      onChange={(e) =>
                        onQueryParamChange(index, "enabled", e.target.checked)
                      }
                      className="h-4 w-4"
                    />
                  </div>
                  {/* Key Input */}
                  <div className="flex flex-col gap-1.5">
                    <Input
                      type="text"
                      value={item.key}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        onQueryParamChange(index, "key", e.target.value);
                      }}
                      placeholder="Enter parameter key (e.g., api_key, user_id)"
                      className="border bg-background text-xs rounded-md"
                    />
                  </div>

                  {/* Value Input */}
                  <div className="flex flex-col gap-1.5">
                    <Input
                      type="text"
                      value={item.value}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onQueryParamChange(index, "value", e.target.value)
                      }
                      placeholder="Enter parameter value"
                      className="border bg-background text-xs rounded-md"
                    />
                  </div>

                  <button
                    onClick={() => onRemoveQueryParam(index)}
                    className="text-destructive hover:text-destructive/80"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between gap-4 m-2 px-2">
              <button
                onClick={onAddQueryParam}
                className="text-sm text-primaryBlue hover:text-muted-foreground flex items-center gap-1"
              >
                <CirclePlus size={14} /> Add Row
              </button>
              {
                <button
                  onClick={clearQueryParam}
                  className="text-sm text-destructive hover:text-muted-foreground flex items-center gap-1"
                >
                  <CircleMinus size={14} /> Clear All Query Params
                </button>
              }
            </div>
          </div>
        </>
      ) : null}

      {/* pagination types  */}

      {visible && paginationType && paginationType !== "none" && (
        <>
          <span className="font-bold text-md ml-1 mt-2 text-primaryAmber block">
            Pagination
          </span>
          <div className="border border-amberBorder bg-bgAmber rounded-lg p-2 mt-1 flex flex-col gap-4">
            {/* ... rest of your JSX stays exactly the same ... */}

            <div className="grid grid-cols-[auto_1fr_1fr_auto] gap-2 mb-0 mt-2 text-xs font-semibold text-muted-foreground">
              <div className="w-6" />
              {isOffsetLimit ? (
                <>
                  <div>Offset Configuration</div>
                  {/* <div>Limit Configuration</div> */}
                </>
              ) : isPageSize ? (
                <>
                  <div>Page Configuration</div>
                  {/* <div>Size Configuration</div> */}
                </>
              ) : isManualKeyValue ? (
                <>
                  <div>Key</div>
                  <div>Value</div>
                </>
              ) : (
                <>
                  <div>Key Configuration</div>
                  <div>Value Configuration</div>
                </>
              )}
              <div className="w-6" />
            </div>

            {formData.map((item, index) => (
              <div
                key={index}
                className={`grid gap-2 items-center border rounded-md bg-muted/50 p-3 transition-all ${
                  needsVerticalLayout
                    ? "grid-cols-[auto_1fr_1fr_auto] items-start"
                    : "grid-cols-[auto_1fr_1fr_auto] items-center"
                }`}
              >
                <div>
                  <input
                    type="checkbox"
                    checked={item.paginationEnable}
                    onChange={(e) =>
                      onChange(index, "paginationEnable", e.target.checked)
                    }
                    className={`h-4 w-4 ${isOffsetLimit || isPageSize ? "mt-2" : ""}`}
                  />
                </div>
                {/* OFFSET LIMIT */}
                {isOffsetLimit && (
                  <>
                    {/* Offset Column */}
                    <div className="flex flex-col items-start justify-start h-full gap-1.5 border-l-4 border-muted-foreground pl-2">
                      <label className="text-xs text-muted-foreground font-medium">
                        Key
                      </label>
                      <Select
                        value={item.categoryTypeKey}
                        onValueChange={(value: string) => {
                          onChange(index, "categoryTypeKey", value);
                          // Track selection order
                          selectionOrderRef.current.set(
                            `${index}-categoryType`,
                            Date.now(),
                          );
                        }}
                      >
                        <SelectTrigger className="border p-2 bg-background text-xs rounded-md">
                          <SelectValue placeholder="Select Type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="offset">offset</SelectItem>
                          <SelectItem value="skip">skip</SelectItem>
                          <SelectItem value="start">start</SelectItem>
                          <SelectItem value="custom">custom</SelectItem>
                        </SelectContent>
                      </Select>

                      {item.categoryTypeKey === "custom" && (
                        <Input
                          type="text"
                          value={item.categoryTypeCustom || ""}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            onChange(
                              index,
                              "categoryTypeCustom",
                              e.target.value,
                            )
                          }
                          placeholder="Enter custom offset key"
                          className="border p-2 bg-background text-xs rounded-md"
                        />
                      )}

                      <label className="text-xs text-muted-foreground font-medium mt-1">
                        Value
                      </label>
                      <Input
                        type="text"
                        value={item.categoryTypeValue || ""}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          onChange(index, "categoryTypeValue", e.target.value);
                        }}
                        placeholder={
                          item.categoryTypeKey === "custom"
                            ? `Custom  value (e.g., 0)`
                            : item.categoryTypeKey
                              ? `${item.categoryTypeKey.charAt(0).toUpperCase() + item.categoryTypeKey.slice(1)} value (e.g., 0)`
                              : "value (e.g., 0)"
                        }
                        className="border p-2 bg-background text-xs rounded-md"
                      />
                    </div>

                    {/* Limit Column */}
                    <div className="flex flex-col items-start justify-start h-full gap-1.5">
                      <label className="text-xs text-muted-foreground font-medium">
                        Key
                      </label>
                      <Select
                        value={item.paginationTypeKey}
                        onValueChange={(value: string) => {
                          onChange(index, "paginationTypeKey", value);
                          // Track selection order
                          selectionOrderRef.current.set(
                            `${index}-categoryValue`,
                            Date.now(),
                          );
                        }}
                      >
                        <SelectTrigger className="border p-2 bg-background text-xs rounded-md">
                          <SelectValue placeholder="Select Type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="limit">limit</SelectItem>
                          <SelectItem value="count">count</SelectItem>
                          <SelectItem value="pageSize">pageSize</SelectItem>
                          <SelectItem value="perPage">perPage</SelectItem>
                          <SelectItem value="custom">custom</SelectItem>
                        </SelectContent>
                      </Select>

                      {item.paginationTypeKey === "custom" && (
                        <Input
                          type="text"
                          value={item.paginationTypeCustom || ""}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            onChange(
                              index,
                              "paginationTypeCustom",
                              e.target.value,
                            )
                          }
                          placeholder="Enter custom limit key"
                          className="border p-2 bg-background text-xs rounded-md"
                        />
                      )}

                      <label className="text-xs text-muted-foreground font-medium mt-1">
                        Value
                      </label>
                      <Input
                        type="text"
                        value={item.paginationTypeValue || ""}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          onChange(
                            index,
                            "paginationTypeValue",
                            e.target.value,
                          );
                        }}
                        placeholder={
                          item.paginationTypeKey === "pageSize"
                            ? "Page size value (e.g., 100)"
                            : item.paginationTypeKey === "perPage"
                              ? "Per page value (e.g., 100)"
                              : item.paginationTypeKey &&
                                  item.paginationTypeKey !== "custom"
                                ? `${item.paginationTypeKey.charAt(0).toUpperCase() + item.paginationTypeKey.slice(1)} value (e.g., 100)`
                                : "Limit value (e.g., 100)"
                        }
                        className="border p-2 bg-background text-xs rounded-md"
                      />
                    </div>
                  </>
                )}

                {/* PAGE SIZE */}
                {isPageSize && (
                  <>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs text-muted-foreground font-medium">
                        Key
                      </label>
                      <Select
                        value={item.categoryTypeKey}
                        onValueChange={(value: string) => {
                          onChange(index, "categoryTypeKey", value);
                          // Track selection order
                          selectionOrderRef.current.set(
                            `${index}-categoryType`,
                            Date.now(),
                          );
                        }}
                      >
                        <SelectTrigger className="border p-2 bg-background text-xs rounded-md">
                          <SelectValue placeholder="Select Type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="page">page</SelectItem>
                          <SelectItem value="p">p</SelectItem>
                          <SelectItem value="pageNumber">pageNumber</SelectItem>
                          <SelectItem value="custom">custom</SelectItem>
                        </SelectContent>
                      </Select>

                      {item.categoryTypeKey === "custom" && (
                        <Input
                          type="text"
                          value={item.categoryTypeCustom || ""}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            onChange(
                              index,
                              "categoryTypeCustom",
                              e.target.value,
                            )
                          }
                          placeholder="Enter custom page key"
                          className="border p-2 bg-background text-xs rounded-md"
                        />
                      )}

                      <label className="text-xs text-muted-foreground font-medium mt-1">
                        Value
                      </label>
                      <Input
                        type="text"
                        value={item.categoryTypeValue || ""}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          onChange(index, "categoryTypeValue", e.target.value);
                        }}
                        placeholder={
                          item.categoryTypeKey === "custom"
                            ? `Custom page value (e.g., 1)`
                            : item.categoryTypeKey
                              ? `${item.categoryTypeKey.charAt(0).toUpperCase() + item.categoryTypeKey.slice(1)} value (e.g., 1)`
                              : "Page value (e.g., 1)"
                        }
                        className="border p-2 bg-background text-xs rounded-md"
                      />
                    </div>

                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs text-muted-foreground font-medium">
                        Key
                      </label>
                      <Select
                        value={item.paginationTypeKey}
                        onValueChange={(value: string) => {
                          onChange(index, "paginationTypeKey", value);
                          // Track selection order
                          selectionOrderRef.current.set(
                            `${index}-categoryValue`,
                            Date.now(),
                          );
                        }}
                      >
                        <SelectTrigger className="border p-2 bg-background text-xs rounded-md">
                          <SelectValue placeholder="Select Type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="size">size</SelectItem>
                          <SelectItem value="pageSize">pageSize</SelectItem>
                          <SelectItem value="perPage">perPage</SelectItem>
                          <SelectItem value="custom">custom</SelectItem>
                        </SelectContent>
                      </Select>

                      {item.paginationTypeKey === "custom" && (
                        <Input
                          type="text"
                          value={item.paginationTypeCustom || ""}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            onChange(
                              index,
                              "paginationTypeCustom",
                              e.target.value,
                            )
                          }
                          placeholder="Enter custom size key"
                          className="border p-2 bg-background text-xs rounded-md"
                        />
                      )}

                      <label className="text-xs text-muted-foreground font-medium mt-1">
                        Value
                      </label>
                      <Input
                        type="text"
                        value={item.paginationTypeValue || ""}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          onChange(
                            index,
                            "paginationTypeValue",
                            e.target.value,
                          );
                        }}
                        placeholder={
                          item.paginationTypeKey === "pageSize"
                            ? "Page size value (e.g., 50)"
                            : item.paginationTypeKey === "perPage"
                              ? "Per page value (e.g., 50)"
                              : item.paginationTypeKey &&
                                  item.paginationTypeKey !== "custom"
                                ? `${item.paginationTypeKey.charAt(0).toUpperCase() + item.paginationTypeKey.slice(1)} value (e.g., 50)`
                                : "Size value (e.g., 50)"
                        }
                        className="border p-2 bg-background text-xs rounded-md"
                      />
                    </div>
                  </>
                )}

                {isCursor && (
                  <>
                    {/* Limit Configuration */}
                    <div className="flex flex-col items-start justify-start h-full gap-1.5 border-l-4 border-blue-500 pl-2">
                      <label className="text-xs text-muted-foreground font-medium">
                        Key
                      </label>
                      <Select
                        value={item.limitKey || ""}
                        onValueChange={(value: string) => {
                          onChange(index, "limitKey" as any, value);
                        }}
                      >
                        <SelectTrigger className="border p-2 bg-background text-xs rounded-md">
                          <SelectValue placeholder="Select Type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="limit">limit</SelectItem>
                          <SelectItem value="custom">custom</SelectItem>
                        </SelectContent>
                      </Select>

                      {item.limitKey === "custom" && (
                        <>
                          <label className="text-xs text-muted-foreground font-medium mt-2">
                            Custom Key
                          </label>
                          <Input
                            type="text"
                            value={item.limitCustomKey || ""}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) =>
                              onChange(
                                index,
                                "limitCustomKey" as any,
                                e.target.value,
                              )
                            }
                            placeholder="Enter Custom Cursor Key"
                            className="border p-2 bg-background text-xs rounded-md"
                          />
                        </>
                      )}

                      <label className="text-xs text-muted-foreground font-medium mt-2">
                        Value
                      </label>
                      <Input
                        type="text"
                        value={item.limitValue || ""}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          onChange(index, "limitValue" as any, e.target.value)
                        }
                        placeholder="Enter Value (e.g., 10, 20, 50)"
                        className="border p-2 bg-background text-xs rounded-md"
                      />
                    </div>

                    {/* Cursor Configuration - Only show after limit is configured */}

                    <div className="flex flex-col items-start justify-start h-full gap-1.5">
                      <div className="flex items-center gap-1.5">
                        <label className="text-xs text-muted-foreground font-medium">
                          Response Path
                        </label>

                        <CustomTooltip text='Path to the pagination value in the response (e.g., "pagination.nextCursor"). Used to fetch the next page.'>
                          <InfoIcon className="w-3.5 h-3.5 text-muted-foreground cursor-pointer" />
                        </CustomTooltip>
                      </div>
                      <Input
                        type="text"
                        value={item.cursorSourcePath || ""}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          onChange(
                            index,
                            "cursorSourcePath" as any,
                            e.target.value,
                          )
                        }
                        placeholder="e.g., data.pagination.cursor or response.nextCursor"
                        className="border p-2 bg-background text-xs rounded-md"
                      />

                      {/* Show cursor key and value inputs only after source path is entered */}

                      <>
                        <div className="flex items-center gap-1 mt-2">
                          <label className="text-xs text-muted-foreground font-medium">
                            Cursor Key
                          </label>

                          <CustomTooltip text="Enter the response key that contains the cursor value (e.g., nextCursor). This value will be used to fetch the next page.">
                            <InfoIcon className="w-3.5 h-3.5 text-muted-foreground cursor-pointer" />
                          </CustomTooltip>
                        </div>

                        <Input
                          type="text"
                          value={item.cursorKey || ""}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            onChange(index, "cursorKey" as any, e.target.value)
                          }
                          placeholder="Cursor Parameter Name (e.g., cursor, next_Cusror)"
                          className="border p-2 bg-background text-xs rounded-md"
                        />
                        {/* <p className="text-xs text-muted-foreground mt-1 italic">
                          💡 Cursor value will be automatically extracted from response
                        </p> */}

                        {/* Cursor Value - Hidden, extracted from response automatically */}
                        {false && (
                          <>
                            <label className="text-xs text-muted-foreground font-medium mt-2">
                              Cursor Value
                            </label>
                            <Input
                              type="text"
                              value={item.cursorValue || ""}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                              ) =>
                                onChange(
                                  index,
                                  "cursorValue" as any,
                                  e.target.value,
                                )
                              }
                              placeholder="Enter cursor value manually"
                              className="border p-2 bg-background text-xs rounded-md"
                            />
                          </>
                        )}
                      </>
                    </div>
                  </>
                )}

                {/* TOKEN PAGINATION */}
                {isToken && (
                  <>
                    {/* Limit Configuration */}
                    <div className="flex flex-col items-start justify-start h-full gap-1.5 border-l-4 border-purple-500 pl-2">
                      <label className="text-xs text-muted-foreground font-medium">
                        Key
                      </label>
                      <Select
                        value={item.limitKey || ""}
                        onValueChange={(value: string) => {
                          onChange(index, "limitKey" as any, value);
                        }}
                      >
                        <SelectTrigger className="border p-2 bg-background text-xs rounded-md">
                          <SelectValue placeholder="Select Type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="limit">limit</SelectItem>
                          <SelectItem value="custom">custom</SelectItem>
                        </SelectContent>
                      </Select>

                      {item.limitKey === "custom" && (
                        <>
                          <label className="text-xs text-muted-foreground font-medium mt-2">
                            Custom Key
                          </label>
                          <Input
                            type="text"
                            value={item.limitCustomKey || ""}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) =>
                              onChange(
                                index,
                                "limitCustomKey" as any,
                                e.target.value,
                              )
                            }
                            placeholder="Enter custom key"
                            className="border p-2 bg-background text-xs rounded-md"
                          />
                        </>
                      )}

                      <label className="text-xs text-muted-foreground font-medium mt-2">
                        Value
                      </label>
                      <Input
                        type="text"
                        value={item.limitValue || ""}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          onChange(index, "limitValue" as any, e.target.value)
                        }
                        placeholder="Enter Value (e.g., 10, 20, 50)"
                        className="border p-2 bg-background text-xs rounded-md"
                      />
                    </div>

                    {/* Token Configuration */}
                    <div className="flex flex-col items-start justify-start h-full gap-1.5">
                      <div className="flex items-center gap-1.5">
                        <label className="text-xs text-muted-foreground font-medium">
                          Response Path
                        </label>

                        <CustomTooltip text='Path used to send the pagination token in the next request (e.g., "pagination.nextToken").'>
                          <InfoIcon className="w-3.5 h-3.5 text-muted-foreground cursor-pointer mt-[1px]" />
                        </CustomTooltip>
                      </div>
                      <Input
                        type="text"
                        value={item.tokenSourcePath || ""}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          onChange(
                            index,
                            "tokenSourcePath" as any,
                            e.target.value,
                          )
                        }
                        placeholder="e.g., data.token or response.nextPageToken"
                        className="border p-2 bg-background text-xs rounded-md"
                      />

                      {/* Show token key and value inputs only after source path is entered */}

                      <>
                        <div className="flex items-center gap-1 mt-2">
                          <label className="text-xs text-muted-foreground font-medium">
                            Token Key
                          </label>

                          <CustomTooltip text="Enter the response key that contains the pagination token (e.g., nextToken). This token will be included in the next request to retrieve more results.">
                            <InfoIcon className="w-3.5 h-3.5 text-muted-foreground cursor-pointer" />
                          </CustomTooltip>
                        </div>
                        <Input
                          type="text"
                          value={item.tokenKey || ""}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            onChange(index, "tokenKey" as any, e.target.value)
                          }
                          placeholder="Token parameter name (e.g., token, pageToken)"
                          className="border p-2 bg-background text-xs rounded-md"
                        />
                        {/* <p className="text-xs text-muted-foreground mt-1 italic">
                          💡 Token value will be automatically extracted from response
                        </p> */}

                        {/* Token Value - Hidden, extracted from response automatically */}
                        {false && (
                          <>
                            <label className="text-xs text-muted-foreground font-medium mt-2">
                              Token Value
                            </label>
                            <Input
                              type="text"
                              value={item.tokenValue || ""}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                              ) =>
                                onChange(
                                  index,
                                  "tokenValue" as any,
                                  e.target.value,
                                )
                              }
                              placeholder="Enter token value manually"
                              className="border p-2 bg-background text-xs rounded-md"
                            />
                          </>
                        )}
                      </>
                    </div>
                  </>
                )}

                {/* NEXT URL PAGINATION */}
                {isNext && (
                  <>
                    {/* Limit Configuration */}
                    <div className="flex flex-col items-start justify-start h-full gap-1.5 border-l-4 border-green-500 pl-2">
                      <label className="text-xs text-muted-foreground font-medium">
                        Key
                      </label>
                      <Select
                        value={item.limitKey || ""}
                        onValueChange={(value: string) => {
                          onChange(index, "limitKey" as any, value);
                        }}
                      >
                        <SelectTrigger className="border p-2 bg-background text-xs rounded-md">
                          <SelectValue placeholder="Select Type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="limit">limit</SelectItem>
                          <SelectItem value="custom">custom</SelectItem>
                        </SelectContent>
                      </Select>

                      {item.limitKey === "custom" && (
                        <>
                          <label className="text-xs text-muted-foreground font-medium mt-2">
                            Custom Key
                          </label>
                          <Input
                            type="text"
                            value={item.limitCustomKey || ""}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) =>
                              onChange(
                                index,
                                "limitCustomKey" as any,
                                e.target.value,
                              )
                            }
                            placeholder="Enter Custom Key"
                            className="border p-2 bg-background text-xs rounded-md"
                          />
                        </>
                      )}

                      <label className="text-xs text-muted-foreground font-medium mt-2">
                        Value
                      </label>
                      <Input
                        type="text"
                        value={item.limitValue || ""}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          onChange(index, "limitValue" as any, e.target.value)
                        }
                        placeholder="Enter Value (e.g., 10, 20, 50)"
                        className="border p-2 bg-background text-xs rounded-md"
                      />
                    </div>

                    {/* Next URL Configuration */}
                    <div className="flex flex-col items-start justify-start h-full gap-1.5">
                      {/* Next URL doesn't need Response Path - the complete next URL comes from API */}

                      <div className="flex items-center gap-1">
                        <label className="text-xs text-muted-foreground font-medium">
                          Next URL Key
                        </label>

                        <CustomTooltip text="Enter the key (e.g., next, nextUrl) used to extract the next-page URL from the API response">
                          <InfoIcon className="w-3.5 h-3.5 text-muted-foreground cursor-pointer" />
                        </CustomTooltip>
                      </div>
                      <Input
                        type="text"
                        value={item.nextUrlKey || ""}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          onChange(index, "nextUrlKey" as any, e.target.value)
                        }
                        placeholder="Next URL Parameter Name (e.g., next, nextUrl)"
                        className="border p-2 bg-background text-xs rounded-md"
                      />

                      {/* Next URL Value - Hidden, extracted from response automatically */}
                      {false && (
                        <>
                          <label className="text-xs text-muted-foreground font-medium mt-2">
                            Next URL Value
                          </label>
                          <Input
                            type="text"
                            value={item.nextUrlValue || ""}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) =>
                              onChange(
                                index,
                                "nextUrlValue" as any,
                                e.target.value,
                              )
                            }
                            placeholder="Enter next URL value manually"
                            className="border p-2 bg-background text-xs rounded-md"
                          />
                        </>
                      )}
                    </div>
                  </>
                )}
                {isManualKeyValue && (
                  <>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs text-muted-foreground font-medium">
                        Key
                      </label>
                      <Input
                        type="text"
                        value={item.paramKey || ""}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          onChange(index, "paramKey" as any, e.target.value)
                        }
                        placeholder="Enter parameter key (e.g., api_key)"
                        className="border p-2 bg-background text-xs rounded-md"
                      />
                    </div>

                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs text-muted-foreground font-medium">
                        Value
                      </label>
                      <Input
                        type="text"
                        value={item.paramValue || ""}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          onChange(index, "paramValue" as any, e.target.value)
                        }
                        placeholder="Enter parameter value"
                        className="border p-2 bg-background text-xs rounded-md"
                      />
                    </div>
                  </>
                )}

                <button
                  onClick={() => onRemoveRow(index)}
                  className={`pl-2 text-destructive hover:text-destructive/80 ${
                    isOffsetLimit || isPageSize ? "mt-2" : ""
                  }`}
                >
                  <X size={16} />
                </button>
              </div>
            ))}

            <div className="flex items-center justify-between gap-4 px-2 py-2">
              <button
                onClick={onAddRow}
                className="text-sm text-primaryBlue hover:text-muted-foreground flex items-center gap-1"
              >
                <CirclePlus size={14} /> Add Row
              </button>
              <button
                onClick={clearPagination}
                className="text-sm text-destructive flex items-center gap-1 hover:text-muted-foreground"
              >
                <CircleMinus size={14} /> Clear Pagination
              </button>
            </div>
          </div>
        </>
      )}
      {/* filteration block section  */}
      {filterationType != "none" && (
        <FilterationBlock
          filterationType={filterationType}
          onFilterationDataChange={(
            startKey,
            endKey,
            customFormat,
            logCollectionPeriod,
          ) => {
            onFilterationChange(
              filterationType,
              startKey,
              endKey,
              customFormat,
              logCollectionPeriod,
            );
          }}
          savedStartKey={savedStartKey}
          savedEndKey={savedEndKey}
          savedCustomDateFormat={savedCustomDateFormat}
          savedLogCollectionPeriod={savedLogCollectionPeriod}
        />
      )}
    </div>
  );
}
